/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/* eslint-disable max-lines */
/* eslint-disable react/boolean-prop-naming */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Subscribe } from 'unstated';

import { AMOUNT_OF_PLACEHOLDERS, IMAGE_TYPE, THUMBNAIL_KEY } from 'Component/ProductGallery/ProductGallery.config';
import SharedTransitionContainer from 'Component/SharedTransition/SharedTransition.unstated';
import { DeviceType } from 'Type/Device.type';
import { ProductType } from 'Type/ProductList.type';
import { formatImageUrl } from 'Util/ImageCDN';

import ProductGalleryComponent from './ProductGallery.component';

/** @namespace Scandipwa/Component/ProductGallery/Container/mapStateToProps */
// eslint-disable-next-line no-unused-vars
export const mapStateToProps = (state) => ({
    device: state.ConfigReducer.device,
    cloudinaryStatus: state.ConfigReducer.isCloudinaryCDNActive
});

/** @namespace Scandipwa/Component/ProductGallery/Container/mapDispatchToProps */
export const mapDispatchToProps = () => ({});

/** @namespace Scandipwa/Component/ProductGallery/Container */
export class ProductGalleryContainer extends PureComponent {
    static propTypes = {
        product: ProductType.isRequired,
        areDetailsLoaded: PropTypes.bool,
        cloudinaryStatus: PropTypes.bool.isRequired,
        device: DeviceType.isRequired
    };

    static defaultProps = {
        areDetailsLoaded: false
    };

    containerFunctions = {
        onActiveImageChange: this.onActiveImageChange.bind(this),
        handleImageClick: this.handleImageClick.bind(this),
        handleOverlayClose: this.handleOverlayClose.bind(this),
        setSliderOverlayWidth: this.setSliderOverlayWidth.bind(this),
        setIsZoomedInState: this.setIsZoomedInState.bind(this)
    };

    static getDerivedStateFromProps(props, state) {
        const { product: { id } } = props;
        const { prevProdId } = state;
        if (prevProdId === id) {
            return null;
        }

        return { prevProdId: id, activeImage: 0 };
    }

    componentDidUpdate(prevProps, prevState) {
        const { activeImage, isSliderOverlay } = this.state;
        const { activeImage: prevActiveImage } = prevState;

        if (activeImage !== prevActiveImage && isSliderOverlay) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                isNextSlide: true
            });
        }

        if (!isSliderOverlay) {
            window.removeEventListener('mouseup', this.handleMouseUp);
            window.removeEventListener('mousedown', this.handleMouseDown);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('mousedown', this.handleMouseDown);
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('touchstart', this.handleTouchStart);
        window.removeEventListener('touchend', this.handleTouchEnd);
    }

    __construct(props) {
        super.__construct(props);

        const { product: { id } } = props;

        this.state = {
            activeImage: 0,
            isZoomEnabled: false,
            prevProdId: id,
            isSliderOverlay: false,
            sliderOverlayWidth: 0,
            zoomPositionX: 0,
            zoomPositionY: 0,
            isPictureBeingMoved: false,
            isZoomDisabled: false,
            isNextSlide: false,
            isDefaultScale: false
        };
    }

    onActiveImageChange(activeImage) {
        this.setState({
            activeImage,
            isZoomEnabled: false
        });
    }

    setIsZoomedInState(isZoomTriggered = false, isSliderOverlay, zoomIn, resetTransform, e) {
        const { isPictureBeingMoved, isDefaultScale } = this.state;
        const { device } = this.props;
        const isDefaultScaleSet = device.isMobile ? isDefaultScale : false;

        if (isPictureBeingMoved && isZoomTriggered) {
            zoomIn(e);
        } else if (!isPictureBeingMoved && !isZoomTriggered) {
            resetTransform(e);
        } else if (!isDefaultScaleSet && !isPictureBeingMoved && isZoomTriggered) {
            zoomIn(e);
        }

        window.addEventListener('touchstart', this.handleTouchStart);
        window.addEventListener('mousedown', this.handleMouseDown);
    }

    handleTouchStart = ({ clientX, clientY }) => {
        window.addEventListener('touchend', this.handleTouchEnd);

        this.setState({
            zoomPositionX: clientX,
            zoomPositionY: clientY,
            isPictureBeingMoved: false,
            isNextSlide: false,
            isDefaultScale: false
        });
    };

    handleTouchEnd = ({ clientX, clientY }) => {
        const { zoomPositionX, zoomPositionY } = this.state;

        // eslint-disable-next-line no-magic-numbers
        if ((Math.abs(zoomPositionX - clientX) > 10 || Math.abs(zoomPositionY - clientY) > 10)) {
            this.setState({
                isPictureBeingMoved: true
            });
        }

        window.removeEventListener('touchend', this.handleTouchEnd);
    };

    handleMouseUp = ({ clientX, clientY }) => {
        const { zoomPositionX, zoomPositionY } = this.state;

        // eslint-disable-next-line no-magic-numbers
        if ((Math.abs(zoomPositionX - clientX) > 10 || Math.abs(zoomPositionY - clientY) > 10)) {
            this.setState({
                isPictureBeingMoved: true
            });
        }

        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseDown = ({ clientX, clientY }) => {
        window.addEventListener('mouseup', this.handleMouseUp);

        this.setState({
            zoomPositionX: clientX,
            zoomPositionY: clientY,
            isPictureBeingMoved: false,
            isNextSlide: false
        });
    };

    setSliderOverlayWidth(sliderWidth) {
        this.setState({
            sliderOverlayWidth: sliderWidth
        });
    }

    handleImageClick() {
        const { isSliderOverlay } = this.state;

        if (!isSliderOverlay) {
            this.setState({
                isSliderOverlay: true,
                isDefaultScale: true
            });
        }

        document.documentElement.classList.add('overlayFullScreen');
    }

    handleOverlayClose() {
        this.setState({
            isSliderOverlay: false,
            sliderOverlayWidth: 0
        });

        document.body.style.position = 'relative';
        document.body.style.overflow = 'initial';
        document.documentElement.classList.remove('overlayFullScreen');
    }

    getGalleryPictures() {
        const {
            areDetailsLoaded,
            device,
            cloudinaryStatus,
            product: {
                media_gallery_entries: mediaGallery = [],
                thumbnail: { url } = {},
                name
            }
        } = this.props;
        const { isSliderOverlay } = this.state;
        const smallImageSize = device.isMobile ? '480' : '706';
        const imageSize = isSliderOverlay ? '1536' : smallImageSize;

        if (mediaGallery.length) {
            return Object.values(mediaGallery.reduce((acc, srcMedia) => {
                const {
                    types,
                    position,
                    disabled,
                    base
                } = srcMedia;

                const canBeShown = !disabled;
                if (!canBeShown) {
                    return acc;
                }

                const isThumbnail = types.includes(THUMBNAIL_KEY);
                const key = isThumbnail ? 0 : position + 1;

                return base && {
                    ...acc,
                    [key]: {
                        ...srcMedia,
                        base: {
                            url: formatImageUrl(
                                cloudinaryStatus,
                                srcMedia.base.url,
                                imageSize
                            )
                        }
                    }
                };
            }, {}));
        }

        if (!url) {
            return Array(AMOUNT_OF_PLACEHOLDERS).fill({ media_type: 'placeholder' });
        }

        const placeholders = !areDetailsLoaded
            ? Array(AMOUNT_OF_PLACEHOLDERS).fill({ media_type: 'placeholder' }) : [];

        return [
            {
                thumbnail: { url },
                base: { url: formatImageUrl(cloudinaryStatus, url, '480') },
                id: THUMBNAIL_KEY,
                label: name,
                media_type: IMAGE_TYPE
            },
            ...placeholders
        ];
    }

    containerProps = () => {
        const {
            activeImage, isZoomEnabled, isSliderOverlay, sliderOverlayWidth, isNextSlide
        } = this.state;
        const { product: { id }, cloudinaryStatus } = this.props;

        return {
            gallery: this.getGalleryPictures(),
            productName: this._getProductName(),
            activeImage,
            isZoomEnabled,
            productId: id,
            isSliderOverlay,
            sliderOverlayWidth,
            cloudinaryStatus,
            isNextSlide
        };
    };

    /**
     * Returns the name of the product this gallery if for
     * @private
     */
    _getProductName() {
        const { product: { name } } = this.props;
        return name;
    }

    render() {
        return (
            <Subscribe to={ [SharedTransitionContainer] }>
                { ({ registerSharedElementDestination }) => (
                    <ProductGalleryComponent
                      registerSharedElementDestination={ registerSharedElementDestination }
                      { ...this.props }
                      { ...this.containerProps() }
                      { ...this.containerFunctions }
                    />
                ) }
            </Subscribe>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductGalleryContainer);
